/**
 * Sender-address display helpers for mail views.
 *
 * The two chain backends disagree on what `from_address` holds: the Tech
 * Support mailbox (services/email/support_mailbox.py) sends the bare address
 * from parseaddr(), while the orders-portal chain (services/email/
 * chain_service.py) passes the raw From header through, which can be
 * `Name <addr@example.com>`. These helpers normalize both so the UI can
 * always surface the real sender address — the whole point is phishing
 * triage, where a friendly display name must never mask the address.
 */

/** Extract the bare email address from a From-style header value.
 *  `Jane Doe <jane@x.com>` → `jane@x.com`; a bare address passes through. */
export function bareEmailAddress(raw?: string | null): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/<([^<>]*)>\s*$/);
  if (match) {
    const inner = match[1].trim();
    return inner || null;
  }
  return trimmed;
}

/** The address to show NEXT TO a sender display name, or null when showing
 *  it would be redundant (no address, no name to distinguish it from, or the
 *  name already IS the address — the backends fall back to the address as
 *  from_name when the header carries no display name). */
export function senderAddressSuffix(
  fromName?: string | null,
  fromAddress?: string | null,
): string | null {
  const addr = bareEmailAddress(fromAddress);
  if (!addr) return null;
  const name = (fromName ?? '').trim();
  // No display name: the UI already shows the address as the primary label.
  if (!name) return null;
  const nameNormalized = (bareEmailAddress(name) ?? name).toLowerCase();
  if (nameNormalized === addr.toLowerCase()) return null;
  return addr;
}
